import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  type CSSProperties,
} from 'react'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'
import { Calendar, ChevronDown } from 'lucide-react'
import {
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
  subYears,
  format,
} from 'date-fns'
import { cn } from '@/lib/utils/helpers'
import 'react-datepicker/dist/react-datepicker.css'
import './advancedDateRangePicker.css'

export type PresetRange = {
  label: string
  getValue: () => [Date, Date]
}

/** Matches `company-edit.component.ts` `ranges` (ngx-daterangepicker) — no “This Week”. */
export const ANGULAR_COMPANY_STATS_PRESETS: PresetRange[] = [
  {
    label: 'Today',
    getValue: () => {
      const d = new Date()
      return [d, d]
    },
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const d = subDays(new Date(), 1)
      return [d, d]
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const t = new Date()
      return [subDays(t, 6), t]
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const t = new Date()
      return [subDays(t, 29), t]
    },
  },
  {
    label: 'This Month',
    getValue: () => {
      const t = new Date()
      return [startOfMonth(t), endOfMonth(t)]
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const m = subMonths(new Date(), 1)
      return [startOfMonth(m), endOfMonth(m)]
    },
  },
  {
    label: 'This Year',
    getValue: () => {
      const t = new Date()
      return [startOfYear(t), endOfYear(t)]
    },
  },
  {
    label: 'Last Year',
    getValue: () => {
      const y = subYears(new Date(), 1)
      return [startOfYear(y), endOfYear(y)]
    },
  },
]

/** Angular `communication-log-list.component.ts` `ranges` — six presets only (no This/Last Year). */
export const ANGULAR_COMMUNICATION_LOG_PRESETS: PresetRange[] =
  ANGULAR_COMPANY_STATS_PRESETS.slice(0, 6)

/**
 * Angular system-announcement add/edit: six ngx-daterangepicker range presets plus three full calendar years
 * (current year and two prior), matching `setDateRanges()`.
 */
export function getAngularSystemAnnouncementPresets(): PresetRange[] {
  const currentYear = new Date().getFullYear()
  const yearPresets: PresetRange[] = []
  for (let i = 0; i < 3; i++) {
    const y = currentYear - i
    yearPresets.push({
      label: ` ${y}`,
      getValue: () => {
        const d = new Date(y, 0, 1)
        return [startOfYear(d), endOfYear(d)]
      },
    })
  }
  return [...ANGULAR_COMMUNICATION_LOG_PRESETS, ...yearPresets]
}

interface AdvancedDateRangePickerProps {
  startDate: Date | null
  endDate: Date | null
  onChange: (startDate: Date | null, endDate: Date | null) => void
  placeholder?: string
  error?: string
  disabled?: boolean
  className?: string
  minDate?: Date
  maxDate?: Date
  customPresets?: PresetRange[]
  showApplyButton?: boolean
  /**
   * When true with `showApplyButton`, clicking a preset only updates the pending range until APPLY.
   * When false (default), presets still commit immediately like ngx-daterangepicker.
   */
  presetDeferUntilApply?: boolean
  /** Where to place APPLY when `showApplyButton` (Angular shows top-right). */
  applyButtonPlacement?: 'top' | 'bottom'
  /** Number of month panels (Angular uses two stacked calendars for long ranges). */
  monthsShown?: number
  /** Stack multiple month panels vertically (flex-column on `.react-datepicker`). */
  stackMonthsVertically?: boolean
  /** Input label: Angular uses `MM-dd-yyyy - MM-dd-yyyy`. */
  displayRangeFormat?: 'default' | 'angularMmddyyyy' | 'angularMmddyy'
  /** Presets in a single column like Angular (default is 2-col on mobile). */
  presetsSingleColumn?: boolean
  /** Optional class on the dropdown panel (width / z-index). */
  dropdownPanelClassName?: string
  /** Hide leading calendar icon (Angular stats field is text-only). */
  hideTriggerIcon?: boolean
  /** Extra classes for the clickable trigger (e.g. match Angular input border). */
  triggerClassName?: string
  /** Stack presets above the calendar instead of side-by-side (use in narrow containers). */
  presetsOnTop?: boolean
  /**
   * Render the popup in a fixed portal on `document.body` so it is not clipped by modal `overflow` / `max-h`.
   * Use in dialogs (e.g. Impact Notes) where the panel would otherwise be cut off.
   */
  dropdownPortalToBody?: boolean
  /**
   * Fixed `z-index` for the portaled panel (defaults to `10020`). Use above high stacking contexts
   * (e.g. filter drawers at `z-[2147483647]`).
   */
  dropdownPortalZIndex?: number
  /**
   * Center the portaled panel inside the nearest filter drawer (`[data-testid$="-filters-drawer"]`).
   * Requires `dropdownPortalToBody`. Falls back to viewport center if no drawer is found.
   */
  dropdownPortalCenterInFilterDrawer?: boolean
}

const defaultPresets: PresetRange[] = [
  {
    label: 'Today',
    getValue: () => {
      const today = new Date()
      return [today, today]
    },
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = subDays(new Date(), 1)
      return [yesterday, yesterday]
    },
  },
  {
    label: 'This Week',
    getValue: () => {
      const today = new Date()
      return [
        startOfWeek(today, { weekStartsOn: 1 }),
        endOfWeek(today, { weekStartsOn: 1 }),
      ]
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = new Date()
      return [subDays(today, 6), today]
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = new Date()
      return [subDays(today, 29), today]
    },
  },
  {
    label: 'This Month',
    getValue: () => {
      const today = new Date()
      return [startOfMonth(today), endOfMonth(today)]
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const lastMonth = subMonths(new Date(), 1)
      return [startOfMonth(lastMonth), endOfMonth(lastMonth)]
    },
  },
  {
    label: 'This Year',
    getValue: () => {
      const today = new Date()
      return [startOfYear(today), endOfYear(today)]
    },
  },
  {
    label: 'Previous Year',
    getValue: () => {
      const lastYear = subYears(new Date(), 1)
      return [startOfYear(lastYear), endOfYear(lastYear)]
    },
  },
]

function isScrollbarGutterClick(event: MouseEvent) {
  const target = event.target
  if (!(target instanceof Element)) return false

  let el: Element | null = target
  while (el) {
    if (el instanceof HTMLElement) {
      const hasVerticalScrollbar = el.scrollHeight > el.clientHeight
      const hasHorizontalScrollbar = el.scrollWidth > el.clientWidth

      if (hasVerticalScrollbar || hasHorizontalScrollbar) {
        const rect = el.getBoundingClientRect()
        const verticalGutterWidth = Math.max(el.offsetWidth - el.clientWidth, 12)
        const horizontalGutterHeight = Math.max(el.offsetHeight - el.clientHeight, 12)
        const clickedVerticalGutter =
          hasVerticalScrollbar &&
          event.clientX >= rect.right - verticalGutterWidth &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        const clickedHorizontalGutter =
          hasHorizontalScrollbar &&
          event.clientY >= rect.bottom - horizontalGutterHeight &&
          event.clientY <= rect.bottom &&
          event.clientX >= rect.left &&
          event.clientX <= rect.right

        if (clickedVerticalGutter || clickedHorizontalGutter) {
          return true
        }
      }
    }

    el = el.parentElement
  }

  return false
}

export function AdvancedDateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholder = 'Select Date Range',
  error,
  disabled = false,
  className,
  minDate,
  maxDate,
  customPresets,
  showApplyButton = false,
  presetDeferUntilApply = false,
  applyButtonPlacement = 'bottom',
  monthsShown = 1,
  stackMonthsVertically = false,
  displayRangeFormat = 'default',
  presetsSingleColumn = false,
  dropdownPanelClassName,
  hideTriggerIcon = false,
  triggerClassName,
  presetsOnTop = false,
  dropdownPortalToBody = false,
  dropdownPortalZIndex = 10020,
  dropdownPortalCenterInFilterDrawer = false,
}: AdvancedDateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [pendingStart, setPendingStart] = useState<Date | null>(null)
  const [pendingEnd, setPendingEnd] = useState<Date | null>(null)
  const [activePreset, setActivePreset] = useState<string | null>(null)
  const [portalStyle, setPortalStyle] = useState<CSSProperties>({})
  const [portalDrawerOverlayStyle, setPortalDrawerOverlayStyle] = useState<CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const portaledPanelRef = useRef<HTMLDivElement>(null)

  const presets = customPresets || defaultPresets

  const dropdownPanelClasses = cn(
    'bg-white rounded-xl shadow-xl border border-gray-200 flex animate-in fade-in zoom-in-95 duration-100 min-w-0 min-h-0',
    'overflow-y-auto',
    monthsShown > 1 && !stackMonthsVertically
      ? 'overflow-x-auto'
      : 'overflow-x-hidden',
    presetsOnTop
      ? 'flex-col w-full'
      : 'flex-col sm:flex-row w-full sm:min-w-[280px]',
    !presetsOnTop &&
      monthsShown > 1 &&
      !stackMonthsVertically &&
      presets.length > 0 &&
      'sm:min-w-[760px]',
    !presetsOnTop &&
      monthsShown > 1 &&
      !stackMonthsVertically &&
      presets.length === 0 &&
      'sm:min-w-[576px]',
    presetsOnTop &&
      monthsShown > 1 &&
      stackMonthsVertically &&
      'w-full max-w-[min(100vw-1rem,22.5rem)]',
    dropdownPortalCenterInFilterDrawer &&
      'w-full max-w-[min(calc(100vw-1.5rem),22.5rem)] max-h-[min(85vh,calc(100%-2rem))]',
    dropdownPanelClassName,
  )

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync pending range to the committed props each time the picker opens
      setPendingStart(startDate)
      setPendingEnd(endDate)
      setActivePreset(null)
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const updatePortalPosition = () => {
    if (!containerRef.current) return

    if (dropdownPortalCenterInFilterDrawer) {
      const drawer = containerRef.current.closest<HTMLElement>(
        '[data-testid$="-filters-drawer"]',
      )
      if (drawer) {
        const dr = drawer.getBoundingClientRect()
        setPortalDrawerOverlayStyle({
          position: 'fixed',
          top: dr.top,
          left: dr.left,
          width: dr.width,
          height: dr.height,
          zIndex: dropdownPortalZIndex,
        })
        setPortalStyle({})
        return
      }
      // No drawer ancestor — center in viewport
      setPortalDrawerOverlayStyle({
        position: 'fixed',
        inset: 0,
        zIndex: dropdownPortalZIndex,
      })
      setPortalStyle({})
      return
    }

    setPortalDrawerOverlayStyle({})

    const r = containerRef.current.getBoundingClientRect()
    const vh = window.innerHeight
    const vw = window.innerWidth
    /** Preset column (~sm:w-44) + two month grids (7×~2rem cells ×2 + gap); avoids clipping lead/outside-month days */
    const presetColumnMin = !presetsOnTop && presets.length > 0 ? 184 : 0
    const dualCalendarMin =
      monthsShown > 1 && !stackMonthsVertically ? 576 : monthsShown > 1 ? 280 : 0
    let panelMinWidth = Math.max(280, r.width)
    if (monthsShown > 1) {
      panelMinWidth = Math.max(panelMinWidth, presetColumnMin + dualCalendarMin)
    }
    const maxPanelWidth = Math.min(monthsShown > 1 ? 960 : 720, vw - 16)
    const width = Math.min(Math.max(panelMinWidth, r.width), maxPanelWidth)
    let left = r.left
    if (left + width > vw - 8) left = Math.max(8, vw - 8 - width)
    if (left < 8) left = 8

    const GAP = 8
    const spaceBelow = vh - r.bottom - GAP
    const spaceAbove = r.top - GAP
    // Open on whichever side has more free space (handles trigger near bottom of the viewport)
    const placeBelow = spaceBelow >= spaceAbove
    const available = placeBelow ? spaceBelow : spaceAbove
    // Cap height to the open gap + global limits (panel scrolls inside if content is taller)
    const inner = Math.floor(
      Math.min(700, vh * 0.9, Math.max(0, available - 4)),
    )
    const maxH = inner < 1 ? 80 : inner

    const next: CSSProperties = {
      position: 'fixed',
      left,
      width,
      zIndex: dropdownPortalZIndex,
      maxHeight: maxH,
      minHeight: 0,
      /** Hidden clipped the second month’s leading cells when presets + dual calendar exceeded computed width */
      overflowX: monthsShown > 1 ? 'auto' : 'hidden',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
    }
    if (placeBelow) {
      next.top = r.bottom + GAP
      next.bottom = 'auto'
    } else {
      next.top = 'auto'
      // Distance from bottom of viewport to the gap above the trigger
      next.bottom = vh - r.top + GAP
    }
    setPortalStyle(next)
  }

  useLayoutEffect(() => {
    if (!isOpen || !dropdownPortalToBody) return
    updatePortalPosition()
    const onReposition = () => updatePortalPosition()
    window.addEventListener('scroll', onReposition, true)
    window.addEventListener('resize', onReposition)
    return () => {
      window.removeEventListener('scroll', onReposition, true)
      window.removeEventListener('resize', onReposition)
    }
  }, [
    isOpen,
    dropdownPortalToBody,
    dropdownPortalCenterInFilterDrawer,
    dropdownPortalZIndex,
    monthsShown,
    presetsOnTop,
    stackMonthsVertically,
    presets.length,
  ])

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node
      if (containerRef.current?.contains(t)) return
      if (portaledPanelRef.current?.contains(t)) return
      if (isScrollbarGutterClick(event)) return
      setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handlePresetClick = (preset: PresetRange) => {
    const [start, end] = preset.getValue()
    if (showApplyButton) {
      setPendingStart(start)
      setPendingEnd(end)
      setActivePreset(preset.label)
      if (presetDeferUntilApply) {
        return
      }
      onChange(start, end)
      setIsOpen(false)
      return
    }
    onChange(start, end)
    setIsOpen(false)
  }

  const handleCalendarChange = (dates: [Date | null, Date | null] | null) => {
    if (dates) {
      const [start, end] = dates
      setPendingStart(start)
      setPendingEnd(end)
      setActivePreset(null)
      if (!showApplyButton) {
        onChange(start, end)
        if (start && end) {
          setIsOpen(false)
        }
      }
    }
  }

  const handleApply = () => {
    onChange(pendingStart, pendingEnd)
    setIsOpen(false)
  }

  const formatDisplayDate = () => {
    const angularPattern =
      displayRangeFormat === 'angularMmddyy'
        ? 'MM/dd/yy'
        : displayRangeFormat === 'angularMmddyyyy'
          ? 'MM-dd-yyyy'
          : null
    if (startDate && endDate) {
      if (angularPattern) {
        return `${format(startDate, angularPattern)} - ${format(endDate, angularPattern)}`
      }
      return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
    }
    if (startDate) {
      if (angularPattern) {
        return `${format(startDate, angularPattern)} - …`
      }
      return `${format(startDate, 'MMM dd, yyyy')} - Select End Date`
    }
    return ''
  }

  function renderDateRangePanelContent() {
    return (
      <>
        {/* Left/Top: Presets — `presetsSingleColumn` must be a vertical stack; never use multi-col grid in sm:w-36 (causes overlap). */}
        <div
          className={cn(
            'border-gray-100 bg-slate-50/80 p-2 shrink-0',
            presetsOnTop
              ? 'w-full border-b'
              : cn(
                  'w-full border-b sm:border-b-0 sm:border-r',
                  presetsSingleColumn ? 'sm:min-w-[11rem] sm:w-44' : 'sm:w-36',
                ),
          )}
        >
          <div
            className={cn(
              'gap-1',
              presetsOnTop &&
                presetsSingleColumn &&
                'flex flex-col',
              presetsOnTop &&
                !presetsSingleColumn &&
                'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
              !presetsOnTop && presetsSingleColumn && 'flex flex-col',
              !presetsOnTop &&
                !presetsSingleColumn &&
                'grid grid-cols-2 sm:grid-cols-1',
            )}
          >
            {presets.map((preset, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  'text-left text-xs px-2 py-1.5 rounded-md transition-all font-medium',
                  presetsSingleColumn
                    ? 'w-full whitespace-normal leading-snug'
                    : 'whitespace-nowrap',
                  activePreset === preset.label
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-white hover:text-blue-600 hover:shadow-sm text-gray-600',
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right: calendar(s) + Apply (Angular: APPLY top-right, two months stacked) */}
        <div className="bg-white flex-1 flex flex-col min-w-0 min-h-0">
          {showApplyButton && applyButtonPlacement === 'top' && (
            <div className="flex justify-end px-3 pt-3 pb-2 border-b border-gray-100 shrink-0">
              <button
                type="button"
                onClick={handleApply}
                disabled={!pendingStart || !pendingEnd}
                className="px-6 py-2 bg-[#1da0ff] hover:bg-[#1da0ff]/90 text-white text-xs font-semibold rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed uppercase tracking-wide"
              >
                Apply
              </button>
            </div>
          )}
          <div
            className={cn(
              'p-2 flex justify-center custom-calendar-wrapper',
              stackMonthsVertically && monthsShown > 1 && 'multi-month-stack',
            )}
          >
            <div className="w-fit mx-auto">
              <DatePicker
                inline
                selected={pendingStart}
                onChange={handleCalendarChange}
                startDate={pendingStart}
                endDate={pendingEnd}
                selectsRange
                minDate={minDate}
                maxDate={maxDate}
                monthsShown={monthsShown}
                renderCustomHeader={({
                  date,
                  monthDate,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => {
                  const labelDate = monthDate ?? date
                  return (
                    <div className="grid w-full grid-cols-[auto_1fr_auto] items-center px-2 py-1">
                      <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <ChevronDown className="h-4 w-4 rotate-90 text-slate-600" />
                      </button>

                      <span className="text-center text-sm font-semibold whitespace-nowrap text-slate-700">
                        {format(labelDate, 'MMM yyyy')}
                      </span>

                      <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <ChevronDown className="h-4 w-4 -rotate-90 text-slate-600" />
                      </button>
                    </div>
                  )
                }}
              />
            </div>
          </div>
          {showApplyButton && applyButtonPlacement === 'bottom' && (
            <div className="flex justify-end px-4 py-2 border-t border-gray-100 shrink-0">
              <button
                type="button"
                onClick={handleApply}
                disabled={!pendingStart || !pendingEnd}
                className="px-5 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed uppercase tracking-wide"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {/* Input Trigger */}
      <div
        data-report-date-range-trigger
        className={cn(
          'flex items-center justify-between w-full px-4 py-2.5 border rounded-lg bg-white cursor-pointer transition-all',
          'hover:border-blue-400 focus:ring-2 focus:ring-blue-100',
          error ? 'border-red-500' : 'border-gray-300',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50',
          triggerClassName,
          isOpen && 'ring-2 ring-blue-100 border-[#1da0ff]',
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          {!hideTriggerIcon ? (
            <Calendar className="h-4 w-4 text-gray-500 shrink-0" />
          ) : null}
          <span
            className={cn('text-sm truncate', !startDate && 'text-gray-400')}
          >
            {formatDisplayDate() || placeholder}
          </span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown Panel – compact layout, single month, no scroller (Angular-style) */}
      {isOpen &&
        (dropdownPortalToBody ? (
          dropdownPortalCenterInFilterDrawer ? (
            createPortal(
              <div
                style={portalDrawerOverlayStyle}
                className="flex items-center justify-center p-4 bg-slate-900/30"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setIsOpen(false)
                }}
              >
                <div ref={portaledPanelRef} className={dropdownPanelClasses}>
                  {renderDateRangePanelContent()}
                </div>
              </div>,
              document.body,
            )
          ) : (
            createPortal(
              <div
                ref={portaledPanelRef}
                style={portalStyle}
                className={dropdownPanelClasses}
              >
                {renderDateRangePanelContent()}
              </div>,
              document.body,
            )
          )
        ) : (
          <div
            className={cn(
              dropdownPanelClasses,
              'absolute top-full left-0 z-[200] mt-2 origin-top-left max-w-[calc(100vw-1rem)] max-h-[85vh]',
              presetsOnTop
                ? 'w-full'
                : 'w-[calc(100vw-2rem)] sm:w-auto',
            )}
          >
            {renderDateRangePanelContent()}
          </div>
        ))}
    </div>
  )
}
